import { useRef, useState } from 'react'

import { cloneLayouts } from '../useTrackingPageLayout/helpers'
import useUndoRedoShortcuts from '../useUndoRedoShortcuts/useUndoRedoShortcuts'
import { layoutHistoryActionTypes } from './constants'

const useLayoutHistory = ({ layouts, viewMode, setLayouts, setViewMode }) => {
  const [undoEntries, setUndoEntries] = useState([])
  const [redoEntries, setRedoEntries] = useState([])
  const pendingLayoutsRef = useRef(null)

  const canUndo = undoEntries.length > 0
  const canRedo = redoEntries.length > 0

  const pushUndoEntry = (type, data) => {
    setUndoEntries(previousEntries => [...previousEntries, { type, data }])
  }

  const pushRedoEntry = (type, data) => {
    setRedoEntries(previousEntries => [...previousEntries, { type, data }])
  }

  const recordLayoutChange = previousLayouts => {
    pushUndoEntry(layoutHistoryActionTypes.changeLayout, { layouts: previousLayouts })
    setRedoEntries([])
  }

  const recordViewModeChange = previousViewMode => {
    pushUndoEntry(layoutHistoryActionTypes.changeLayoutType, { viewMode: previousViewMode })
    setRedoEntries([])
  }

  const beginLayoutChange = pendingLayouts => {
    pendingLayoutsRef.current = pendingLayouts
  }

  const commitLayoutChange = hasChanges => {
    const pendingLayouts = pendingLayoutsRef.current

    pendingLayoutsRef.current = null

    if (!pendingLayouts) return

    if (hasChanges(pendingLayouts)) recordLayoutChange(pendingLayouts)
  }

  const getCurrentEntryData = type => {
    if (type === layoutHistoryActionTypes.changeLayout) return { layouts: cloneLayouts(layouts) }

    return { viewMode }
  }

  const applyEntry = entry => {
    if (entry.type === layoutHistoryActionTypes.changeLayout) {
      setLayouts(entry.data.layouts)
      return
    }

    setViewMode(entry.data.viewMode)
  }

  const undo = () => {
    if (undoEntries.length === 0) return

    const lastEntry = undoEntries[undoEntries.length - 1]

    setUndoEntries(previousEntries => previousEntries.slice(0, -1))
    pushRedoEntry(lastEntry.type, getCurrentEntryData(lastEntry.type))
    applyEntry(lastEntry)
  }

  const redo = () => {
    if (redoEntries.length === 0) return

    const lastEntry = redoEntries[redoEntries.length - 1]

    setRedoEntries(previousEntries => previousEntries.slice(0, -1))
    pushUndoEntry(lastEntry.type, getCurrentEntryData(lastEntry.type))
    applyEntry(lastEntry)
  }

  useUndoRedoShortcuts({ onUndo: undo, onRedo: redo })

  return {
    canUndo,
    canRedo,
    recordLayoutChange,
    recordViewModeChange,
    beginLayoutChange,
    commitLayoutChange,
    undo,
    redo,
  }
}

export default useLayoutHistory
